import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { CourseCard } from "@/components/CourseCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, Check, Upload, Loader2, FileText, LineChart, Globe, MessageSquare, GraduationCap, Lightbulb, PoundSterling, Megaphone, PlayCircle, Filter, Sparkles } from "lucide-react";
import { SERVICES, GENERAL_SERVICE } from "@/data/businessServices";

const BANNER = "https://customer-assets.emergentagent.com/job_enrol-progress/artifacts/erj4ooiw_virtual%20speedy%20services%20business%20card.jpeg";
const ICONS = { FileText, LineChart, Globe, MessageSquare, GraduationCap, Lightbulb, PoundSterling, Megaphone, PlayCircle, Filter, Sparkles };

const inputCls = "w-full px-4 py-2.5 border border-[var(--line)] focus:border-[#1E6FEB] outline-none text-sm rounded-md";
const label = "text-xs font-semibold text-[var(--ink-2)] mb-1 block";

function EnquiryForm({ service, onDone }) {
  const [core, setCore] = useState({ full_name: "", business_name: "", email: "", phone: "" });
  const [extra, setExtra] = useState({});
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const setC = (k, v) => setCore((p) => ({ ...p, [k]: v }));
  const setE = (k, v) => setExtra((p) => ({ ...p, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/enquiries/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileUrl(data.file_url); setFileName(data.name);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!core.full_name.trim() || !core.email.trim() || !core.phone.trim()) { toast.error("Please fill in your name, email and phone."); return; }
    const descField = service.fields.find((f) => f.desc);
    const webField = service.fields.find((f) => f.web);
    const description = descField ? (extra[descField.label] || "") : "";
    if (descField && !description.trim()) { toast.error("Please describe what you need."); return; }
    if (!consent) { toast.error("Please tick the consent box so we can contact you."); return; }
    const form_data = {};
    service.fields.forEach((f) => { if (!f.desc && !f.web && f.type !== "file" && extra[f.label]) form_data[f.label] = extra[f.label]; });
    setLoading(true);
    try {
      const { data } = await api.post("/enquiries", {
        service_type: service.title,
        full_name: core.full_name.trim(), business_name: core.business_name.trim(),
        email: core.email.trim(), phone: core.phone.trim(),
        website: webField ? (extra[webField.label] || "") : "",
        description, form_data,
        gdpr_consent: consent, marketing_consent: marketing,
        file_url: fileUrl, company,
      });
      onDone(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your enquiry.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="enquiry-form">
      <input type="text" name="company" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={label}>Full name *</label><input data-testid="enq-full-name" required value={core.full_name} onChange={(e) => setC("full_name", e.target.value)} className={inputCls} /></div>
        <div><label className={label}>Business name</label><input data-testid="enq-business-name" value={core.business_name} onChange={(e) => setC("business_name", e.target.value)} className={inputCls} /></div>
        <div><label className={label}>Email address *</label><input data-testid="enq-email" required type="email" value={core.email} onChange={(e) => setC("email", e.target.value)} className={inputCls} /></div>
        <div><label className={label}>Phone number *</label><input data-testid="enq-phone" required type="tel" value={core.phone} onChange={(e) => setC("phone", e.target.value)} className={inputCls} /></div>
      </div>
      {service.fields.map((f) => {
        const testid = `enq-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`;
        const lbl = <label className={label}>{f.label}{f.desc ? " *" : f.opt ? " (optional)" : ""}</label>;
        if (f.type === "file") return (
          <div key={f.label}>{lbl}
            <label data-testid={`${testid}-upload`} className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 rounded-md hover:bg-[var(--alt)]">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {fileName || "Choose file"}
              <input type="file" className="hidden" disabled={uploading} onChange={onFile} />
            </label>
          </div>
        );
        if (f.type === "select") return (
          <div key={f.label}>{lbl}
            <select data-testid={testid} value={extra[f.label] || ""} onChange={(e) => setE(f.label, e.target.value)} className={inputCls}>
              <option value="">Please select…</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        );
        if (f.type === "textarea") return (
          <div key={f.label}>{lbl}<textarea data-testid={testid} rows={3} value={extra[f.label] || ""} onChange={(e) => setE(f.label, e.target.value)} className={inputCls} /></div>
        );
        return (
          <div key={f.label}>{lbl}<input data-testid={testid} type={f.type === "date" ? "date" : "text"} value={extra[f.label] || ""} onChange={(e) => setE(f.label, e.target.value)} className={inputCls} /></div>
        );
      })}
      <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
        <input type="checkbox" data-testid="enq-gdpr" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#1E6FEB]" />
        <span>I agree for Virtual Speedy Services UK to contact me about my enquiry. *</span>
      </label>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input type="checkbox" data-testid="enq-marketing" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 accent-[#1E6FEB]" />
        <span>I would like to receive updates about services, courses and business support.</span>
      </label>
      <button type="submit" disabled={loading} data-testid="enq-submit" className="w-full bg-[#1E6FEB] text-white font-semibold py-3 rounded-md hover:bg-[#1558c0] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send My Enquiry"}
      </button>
    </form>
  );
}

function ServiceVideo({ url }) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  const embed = yt ? `https://www.youtube.com/embed/${yt[1]}` : vm ? `https://player.vimeo.com/video/${vm[1]}` : null;
  return (
    <div className="aspect-video w-full mb-2 rounded-lg overflow-hidden bg-black" data-testid="service-video">
      {embed
        ? <iframe src={embed} title="Service video" className="w-full h-full" allow="accelerometer; encrypted-media" allowFullScreen />
        : <video src={url} controls className="w-full h-full object-cover" />}
    </div>
  );
}

function ServiceEnquiryDialog({ service, trigger }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState("");
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDone(""); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-xl">
        <DialogHeader><DialogTitle className="font-display text-xl">{done ? "Enquiry received" : `Request: ${service.title}`}</DialogTitle></DialogHeader>
        {done ? (
          <div className="py-6 text-center" data-testid="enq-confirmation">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#E8F5E9] grid place-items-center mb-4"><Check className="text-[#2E7D32]" size={28} /></div>
            <p className="text-[var(--ink-2)]">{done}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-[var(--ink-2)] -mt-1 mb-1">{service.summary}</p>
            {service.video_url && <ServiceVideo url={service.video_url} />}
            <EnquiryForm service={service} onDone={setDone} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PromotedCourses() {
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    api.get("/courses", { params: { featured: true } })
      .then((r) => setCourses((Array.isArray(r.data) ? r.data : r.data.courses || []).slice(0, 4)))
      .catch(() => {});
  }, []);
  if (!courses.length) return null;
  return (
    <section className="border-t border-[var(--line)] bg-[#F4F8FF]" data-testid="bd-promoted-courses">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#7AC943]">Grow your skills</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-2">Featured courses</h2>
          </div>
          <Link to="/courses" className="text-sm font-semibold inline-flex items-center gap-1.5 text-[#1E6FEB] hover:underline" data-testid="bd-view-all-courses">
            View all courses <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {courses.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </div>
    </section>
  );
}

export default function RequestService() {
  const [services, setServices] = useState(SERVICES);
  useEffect(() => {
    api.get("/services").then((r) => { if (Array.isArray(r.data) && r.data.length) setServices(r.data); }).catch(() => {});
  }, []);
  return (
    <div data-testid="request-service-page" className="bg-white">
      <Seo title="Request Business Support Services | Virtual Speedy Services UK"
        description="Request business plans, cashflow projections, website services, chatbot design, CPD course support, virtual marketing, explainer videos and landing page lead generation from Virtual Speedy Services UK."
        path="/business-development" />

      {/* Full landscape banner header */}
      <a href="tel:02030514338" className="block" data-testid="bd-banner">
        <img src={BANNER} alt="Virtual Speedy Services UK — Want it done stress-free? Take our virtual hand." className="w-full h-auto" />
      </a>

      {/* Hero */}
      <section className="border-b border-[var(--line)] bg-gradient-to-br from-white to-[#F4F8FF]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 lg:py-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#7AC943]">Business Development</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2 text-[var(--ink)]">
            Request a <span className="text-[#1E6FEB]">Service</span>
          </h1>
          <p className="text-lg text-[var(--ink-2)] mt-3 max-w-2xl">Tell us what you need and we'll help you move forward stress-free.</p>
          <div className="mt-6 max-w-3xl border-l-4 border-[#7AC943] pl-4">
            <p className="font-display font-bold text-xl text-[var(--ink)]">Want it done stress-free? Take our virtual hand.</p>
            <p className="text-[var(--ink-2)] mt-2 text-sm leading-relaxed">Virtual Speedy Services UK supports businesses, startups, course providers and entrepreneurs with practical business, marketing, digital and growth support. Select the service you are interested in and complete the enquiry form. We will review your request and contact you with the next steps.</p>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = typeof s.icon === "string" ? (ICONS[s.icon] || Sparkles) : (s.icon || Sparkles);
            return (
              <div key={s.id} className="border border-[var(--line)] rounded-xl overflow-hidden bg-white flex flex-col hover:shadow-lg transition-shadow" data-testid={`service-card-${s.id}`}>
                <div className="aspect-[3/1] bg-[#F4F8FF] overflow-hidden"><img src={s.image_url || s.image} alt={s.title} className="w-full h-full object-cover" /></div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-8 h-8 rounded-lg bg-[#EAF2FE] grid place-items-center text-[#1E6FEB]"><Icon size={17} /></span>
                    <h3 className="font-display font-bold text-lg leading-tight">{s.title}</h3>
                  </div>
                  <p className="text-sm text-[var(--ink-2)] leading-relaxed flex-1">{s.summary}</p>
                  <ServiceEnquiryDialog service={s} trigger={
                    <button data-testid={`request-${s.id}`} className="mt-4 inline-flex items-center justify-center gap-2 bg-[#1E6FEB] text-white font-semibold py-2.5 rounded-md hover:bg-[#1558c0] transition-colors text-sm">
                      Request This Service <ArrowRight size={15} />
                    </button>
                  } />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Promoted courses */}
      <PromotedCourses />

      {/* Final CTA */}
      <section className="border-t border-[var(--line)] bg-[#0B1F3A]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 text-center text-white">
          <h2 className="font-display font-extrabold text-3xl tracking-tight">Not sure what you need?</h2>
          <p className="text-white/70 mt-3">Tell us about your business challenge and we'll guide you to the right service.</p>
          <ServiceEnquiryDialog service={GENERAL_SERVICE} trigger={
            <button data-testid="general-enquiry-btn" className="mt-6 inline-flex items-center gap-2 bg-[#7AC943] text-[#0B1F3A] font-bold px-7 py-3.5 rounded-md hover:bg-[#6bb838] transition-colors">
              General Enquiry <ArrowRight size={17} />
            </button>
          } />
        </div>
      </section>
    </div>
  );
}
